import React from 'react';
import { Dialog, Box, Typography } from '@mui/material';

/**
 * Gestar Design System v1 — DialogShell
 * Wrapper consistente para todos los dialogs del sistema.
 * Header diferenciado (#1a2744), content (#16213e), footer (#131e36).
 *
 * Props:
 *   open, onClose       — control del dialog
 *   title               — texto principal del header
 *   subtitle            — texto secundario opcional
 *   maxWidth            — "xs" | "sm" | "md" | "lg" (default "sm")
 *   navigation          — nodo React entre header y contenido (ej: Tabs)
 *   actions             — nodo React para el footer (botones derecha)
 *   actionsStart        — nodo React alineado a la izquierda del footer (ej: botón Eliminar)
 *   children            — contenido del cuerpo
 */
export default function DialogShell({
  open,
  onClose,
  title,
  subtitle,
  maxWidth = 'sm',
  navigation,
  actions,
  actionsStart,
  children,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#16213e',
          backgroundImage: 'none',
          color: '#e0e0e0',
          borderRadius: 2,
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: '#1a2744',
          px: '20px',
          py: '16px',
          borderBottom: '1px solid rgba(144,202,249,0.08)',
        }}
      >
        <Typography sx={{ fontSize: 17, fontWeight: 700, color: '#e0e0e0', lineHeight: 1.3 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: 12, color: '#64748b', mt: 0.5, lineHeight: 1.4 }}>
            {subtitle}
          </Typography>
        )}
      </Box>

      {/* Navigation (tabs, etc.) — full width, alineada con el header */}
      {navigation && (
        <Box sx={{ bgcolor: '#1a2744', px: '20px', borderBottom: '1px solid rgba(144,202,249,0.08)' }}>
          {navigation}
        </Box>
      )}

      {/* Content */}
      <Box sx={{ px: '20px', py: '20px' }}>
        {children}
      </Box>

      {/* Footer */}
      {(actions || actionsStart) && (
        <Box
          sx={{
            bgcolor: '#131e36',
            px: '20px',
            py: '10px',
            borderTop: '1px solid rgba(144,202,249,0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box>{actionsStart}</Box>
          <Box sx={{ display: 'flex', gap: 1 }}>{actions}</Box>
        </Box>
      )}
    </Dialog>
  );
}
